class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
    this.editor = this.dataset.editor === 'true';
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.querySelectorAll('[data-dismiss]').forEach(button => button.addEventListener('click', () => this.dismiss(), options));
    this.dialog.addEventListener('cancel', event => {
      event.preventDefault();
      this.dismiss();
    }, options);
    this.dialog.addEventListener('close', () => this.restoreScroll(), options);
    this.dialog.addEventListener('click', event => {
      const rect = this.dialog.getBoundingClientRect();
      if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dismiss();
    }, options);
    if (this.editor) {
      if (this.dataset.openInDesignMode === 'true') this.show();
      return;
    }
    this.result = this.querySelector('[data-newsletter-result]')?.dataset.newsletterResult;
    this.returning = Boolean(this.result && location.hash === `#${this.form.id}`);
    if (this.result === 'success') this.writeCookie('newsletter-confirmed-v2', 365);
    if (this.isExcludedPage() || (!this.returning && this.isSuppressed())) return;
    this.attempts = 0;
    document.addEventListener('visibilitychange', () => {
      clearTimeout(this.timer);
      if (!document.hidden && !this.shown) this.schedule();
    }, options);
    this.schedule(this.returning ? 0 : this.delay);
  }

  get delay() {
    return Math.min(30, Math.max(4, Number(this.dataset.delay) || 12)) * 1000;
  }

  readCookie(name) {
    try { return document.cookie.split(';').some(cookie => cookie.trim() === `${name}=true`); }
    catch { return false; }
  }

  writeCookie(name, days) {
    try { document.cookie = `${name}=true; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; }
    catch { }
  }

  isSuppressed() {
    let seen = false;
    try { seen = sessionStorage.getItem('soleplay:newsletter:shown') === 'true'; } catch { }
    return this.dataset.subscribed === 'true' || seen || this.readCookie('newsletter-dismissed-v2') || this.readCookie('newsletter-closed') || this.readCookie('newsletter-confirmed-v2');
  }

  isExcludedPage() {
    return this.dataset.pageType === 'cart' || this.dataset.pageType?.startsWith('customers/') || /\/(?:account|checkout|checkouts|challenge)(?:\/|$)/.test(location.pathname) || /\/pages\/nike-one-piece\/?$/.test(location.pathname);
  }

  schedule(delay = this.delay) {
    clearTimeout(this.timer);
    if (!document.hidden && !this.shown) this.timer = setTimeout(() => this.tryShow(), delay);
  }

  tryShow() {
    if (!this.isConnected || this.shown || document.hidden || (!this.returning && this.isSuppressed())) return;
    const ageGate = document.querySelector('age-verification-popup');
    const busy = document.querySelector('dialog[open], [open] [role="dialog"], body.drawer--is-open, html.overflow-hidden, body.overflow-hidden') || document.documentElement.style.overflow === 'hidden';
    const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    if (busy || editing || (ageGate && !this.readCookie('age-verified'))) {
      if (++this.attempts < 30) this.schedule(2000);
      return;
    }
    this.show();
  }

  show() {
    if (!this.isConnected || this.dialog.open) return;
    this.previousFocus = document.activeElement;
    this.dialog.showModal();
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.scrollLocked = true;
    this.shown = true;
    this.dialog.focus({ preventScroll: true });
    if (!this.editor) {
      try { sessionStorage.setItem('soleplay:newsletter:shown', 'true'); } catch { }
    }
  }

  dismiss() {
    if (!this.dialog.open) return;
    if (!this.editor) this.writeCookie('newsletter-dismissed-v2', Math.min(30, Math.max(1, Number(this.dataset.dismissDays) || 7)));
    this.dialog.close();
    this.restoreScroll();
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
  }

  restoreScroll() {
    if (!this.scrollLocked) return;
    document.documentElement.style.overflow = this.previousOverflow;
    this.scrollLocked = false;
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.events?.abort();
    this.events = null;
    if (this.dialog?.open) this.dialog.close();
    this.restoreScroll();
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
